// Typed API client for /v1/api-keys/*.
//
// Wire shape mirrors backend/app/schemas/api_keys.py. The full token is
// returned exactly once on create (ch_<env>_<64>) and never persisted in
// the list — only the `prefix` is shown afterwards.

import { api } from '@/lib/api';

export type ApiKeyEnv = 'live' | 'test';

export interface ApiKeyOut {
  id: string;
  name: string;
  prefix: string;
  env: ApiKeyEnv;
  // Coarsened to the hour to avoid leaking precise activity timing.
  last_used_at_hour: string | null;
  revoked_at: string | null;
  created_at: string;
}

// Returned ONLY by POST — `full_token` is shown once and not recoverable.
export interface ApiKeyCreateOut extends ApiKeyOut {
  full_token: string;
}

export interface ApiKeyListResponse {
  items: ApiKeyOut[];
}

export interface CreateApiKeyBody {
  name: string;
  env: ApiKeyEnv;
}

// The backend's ApiKeyOut does NOT carry `env` — but the prefix always
// encodes it (`ch_live_…` / `ch_test_…`, enforced by a DB CHECK), so we
// derive it client-side. Keeps the Env badge correct without a wire change.
export function envFromPrefix(prefix: string | undefined | null): ApiKeyEnv {
  return prefix?.startsWith('ch_test_') ? 'test' : 'live';
}

export async function listApiKeys(): Promise<ApiKeyListResponse> {
  const resp = await api.get<ApiKeyListResponse>('/api-keys');
  return {
    ...resp.data,
    items: resp.data.items.map((k) => ({ ...k, env: envFromPrefix(k.prefix) })),
  };
}

export async function createApiKey(body: CreateApiKeyBody): Promise<ApiKeyCreateOut> {
  const resp = await api.post<ApiKeyCreateOut>('/api-keys', body);
  return { ...resp.data, env: envFromPrefix(resp.data.prefix) };
}

export async function revokeApiKey(id: string): Promise<void> {
  await api.delete(`/api-keys/${id}`);
}
